/**
 * Arrow calculator factory and exports
 *
 * Provides type-based dispatch to appropriate arrow calculators
 */

import type { IArrowCalculator, ArrowCalculationConfig } from "./types";
import { PairToAtomCalculator } from "./PairToAtomCalculator";
import { PairToBondCalculator } from "./PairToBondCalculator";
import { BondToAtomCalculator } from "./BondToAtomCalculator";
import { BondToBondCalculator } from "./BondToBondCalculator";
import { RadicalToAtomCalculator } from "./RadicalToAtomCalculator";

// Export all calculator types
export { PairToAtomCalculator } from "./PairToAtomCalculator";
export { PairToBondCalculator } from "./PairToBondCalculator";
export { BondToAtomCalculator } from "./BondToAtomCalculator";
export { BondToBondCalculator } from "./BondToBondCalculator";
export { RadicalToAtomCalculator } from "./RadicalToAtomCalculator";
export type {
  IArrowCalculator,
  ArrowCalculationConfig,
  ArrowEndpoints,
  ArrowCalculator,
} from "./types";

/**
 * Arrow type string identifier
 */
export type ArrowType = "PA" | "PB" | "BA" | "BB" | "RA" | "AUTO";

/**
 * Determine arrow type from source and destination types
 *
 * @param sourceType - Type of source point (lonepair, bond, radical, particle)
 * @param destType - Type of destination point (bond, particle)
 * @returns Arrow type identifier
 */
export function determineArrowType(
  sourceType: string,
  destType: string
): ArrowType {
  if (sourceType === "lonepair" && destType === "particle") {
    return "PA"; // Pair → Atom
  }
  if (sourceType === "lonepair" && destType === "bond") {
    return "PB"; // Pair → Bond
  }
  if (sourceType === "bond" && destType === "particle") {
    return "BA"; // Bond → Atom
  }
  if (sourceType === "bond" && destType === "bond") {
    return "BB"; // Bond → Bond
  }
  if (sourceType === "radical" && destType === "particle") {
    return "RA"; // Radical → Atom
  }

  // Default fallback
  return "AUTO";
}

/**
 * Arrow calculator factory
 *
 * Creates and caches calculator instances, dispatching based on arrow type
 */
export class ArrowCalculatorFactory {
  private calculators: Map<ArrowType, IArrowCalculator> = new Map();

  /**
   * Get calculator for a specific arrow type
   *
   * @param arrowType - Type of arrow (PA, PB, BA, BB, RA, or AUTO)
   * @param config - Optional configuration for auto-detection
   * @returns Arrow calculator instance
   */
  getCalculator(
    arrowType: ArrowType,
    config?: ArrowCalculationConfig
  ): IArrowCalculator {
    // Auto-detect arrow type if needed
    if (arrowType === "AUTO" && config) {
      arrowType = determineArrowType(config.source.type, config.destination.type);
    }

    // Return cached calculator or create new one
    if (!this.calculators.has(arrowType)) {
      this.calculators.set(arrowType, this.createCalculator(arrowType));
    }

    const calculator = this.calculators.get(arrowType);
    if (!calculator) {
      throw new Error(`No calculator found for arrow type: ${arrowType}`);
    }

    return calculator;
  }

  /**
   * Create a new calculator instance for the given type
   */
  private createCalculator(arrowType: ArrowType): IArrowCalculator {
    switch (arrowType) {
      case "PA":
        return new PairToAtomCalculator();
      case "PB":
        return new PairToBondCalculator();
      case "BA":
        return new BondToAtomCalculator();
      case "BB":
        return new BondToBondCalculator();
      case "RA":
        return new RadicalToAtomCalculator();
      default:
        throw new Error(`Unknown arrow type: ${arrowType}`);
    }
  }

  /**
   * Calculate arrow endpoints using type-based dispatch
   *
   * @param config - Arrow calculation configuration
   * @param arrowType - Optional explicit arrow type (auto-detected if not provided)
   * @returns Arrow endpoints
   */
  calculate(config: ArrowCalculationConfig, arrowType: ArrowType = "AUTO") {
    const calculator = this.getCalculator(arrowType, config);
    return calculator.calculate(config);
  }

  /**
   * Clear calculator cache
   */
  clearCache(): void {
    this.calculators.clear();
  }

  /**
   * Get all cached calculator types
   */
  getCachedTypes(): ArrowType[] {
    return Array.from(this.calculators.keys());
  }
}

/**
 * Default singleton factory instance
 */
export const defaultArrowCalculatorFactory = new ArrowCalculatorFactory();

/**
 * Convenience function: Calculate arrow endpoints with automatic type detection
 *
 * @param config - Arrow calculation configuration
 * @returns Arrow endpoints
 */
export function calculateArrowEndpoints(config: ArrowCalculationConfig) {
  return defaultArrowCalculatorFactory.calculate(config);
}
